import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoginOverlay from './LoginOverlay';
import StrategyCanvas from './StrategyCanvas';
import { FaPaperclip } from 'react-icons/fa';
import togetherAIService from '../services/togetherAIService';

function OverviewPage({ isAuthenticated, login, joinWaitlist, setActiveNavItem, onOpenStrategy, onWaitlistClick }) {
  const [prompt, setPrompt] = useState('');
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [generatedResponse, setGeneratedResponse] = useState('');
  const [isCreateMode] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const videoRefs = useRef([]);
  const fileInputRef = useRef(null);

  const videos = [
    { left: '/airballoon.mp4', right: '/billboard.mp4' },
    { left: '/city.mp4', right: '/pirateship.mp4' }
  ];

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      setShowAuthPopup(true);
      return;
    }
    
    if (!prompt.trim()) {
      setApiError('Please enter a prompt');
      return;
    }
    
    setIsGenerating(true);
    setApiError(null);
    setGeneratedResponse('');
    
    try {
      // Check if API key is configured
      const apiKey = process.env.REACT_APP_TOGETHER_API_KEY;
      if (!apiKey || apiKey === 'your_together_ai_api_key_here') {
        // Use test mode if no API key
        console.log('Together.ai API key not configured, using test mode');
      }
      
      // Use the Together.ai service
      const messages = [
        {
          role: 'system',
          content: 'You are a helpful AI assistant for small businesses. Help create strategic business content, provide insights, and assist with growth planning.'
        },
        {
          role: 'user',
          content: prompt
        }
      ];
      
      const result = await togetherAIService.generateChatResponse(messages, {
        maxTokens: 512,
        temperature: 0.7
      });
      
      if (result.success) {
        setGeneratedResponse(result.response);
        console.log('Generated response:', result.response);
        // Open full strategy canvas page
        if (onOpenStrategy) {
          onOpenStrategy(prompt);
        }
      } else {
        setApiError(result.response || 'Unable to generate response. Please try again.');
        console.error('Together.ai Error:', result.error);
      }
      
    } catch (error) {
      console.error('Together.ai API Error:', error);
      setApiError('Connection error: Unable to connect to AI service. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return isImage || isVideo;
    });
    
    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles]);
      console.log('Files attached:', validFiles.map(f => f.name));
    } else {
      alert('Please select only image or video files');
    }
    
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleLogin = async (provider, email = null) => {
    // Prefer joinWaitlist — never treat as session success
    if (typeof joinWaitlist === 'function' && email) {
      await joinWaitlist(email);
      return;
    }
    if (typeof login === 'function') {
      await login(provider, email);
    }
  };

  const handleJoinWaitlist = async (email) => {
    if (typeof joinWaitlist === 'function') {
      await joinWaitlist(email);
      return;
    }
    if (typeof login === 'function') {
      await login('waitlist', email);
    }
  };

  const closeAuthPopup = () => {
    setShowAuthPopup(false);
  };

  const closeCanvas = () => {
    setShowCanvas(false);
  };

  const handleVideoEnded = useCallback(() => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  }, [videos.length]);

  useEffect(() => {
    const leftVideo = videoRefs.current[0];
    const rightVideo = videoRefs.current[1];

    if (leftVideo && rightVideo) {
      leftVideo.addEventListener('ended', handleVideoEnded);
      return () => {
        leftVideo.removeEventListener('ended', handleVideoEnded);
      };
    }
  }, [currentVideoIndex, handleVideoEnded]);


  return (
    <div className={`ai-creative-page ${isCreateMode ? 'create-mode' : ''} ${showCanvas ? 'canvas-mode' : ''}`}>
      {!isCreateMode && !showCanvas && (
        <div className="hero-background">
          <div className="hero-videos">
            <video 
              ref={(el) => videoRefs.current[0] = el}
              className="hero-video hero-video-left" 
              autoPlay 
              muted 
              playsInline
              key={`left-${currentVideoIndex}`}
            >
              <source src={videos[currentVideoIndex].left} type="video/mp4" />
            </video>
            <video 
              ref={(el) => videoRefs.current[1] = el}
              className="hero-video hero-video-right" 
              autoPlay 
              muted 
              playsInline
              key={`right-${currentVideoIndex}`}
            >
              <source src={videos[currentVideoIndex].right} type="video/mp4" />
            </video>
          </div>
          <div className="hero-content">
          <h1 className="hero-title">Local Without Limits</h1>
          <p className="hero-subtitle">AI-powered Small Business Intellegence.</p>
          
          <div className="ai-prompt-container">
            <div className="prompt-input-group">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your business strategy, goals, or marketing needs"
                className="prompt-input"
              />
              <button
                onClick={handleGenerate}
                className="generate-button"
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Launch'}
              </button>
            </div>

            {apiError && (
              <div className="api-error-message">
                {apiError}
              </div>
            )}

            {!isCreateMode && generatedResponse && (
              <div className="generated-response">
                <h3>AI Business Insight:</h3>
                <p>{generatedResponse}</p>
              </div>
            )}
          </div>


        </div>

        <div className="integration-logos-bottom">
          <img src="/toastlogo.png" alt="Toast" className="integration-logo-simple" />
          <img src="/shopify.png" alt="Shopify" className="integration-logo-simple" />
          <img src="/squarepost.png" alt="Square" className="integration-logo-simple" />
        </div>
      </div>
      )}

      {showCanvas && (
        <div className="inline-canvas-container">
          <StrategyCanvas
            initialPrompt={prompt}
            onClose={closeCanvas}
            inline={true}
          />
        </div>
      )}
      
      {isCreateMode && (
        <div className="create-mode-layout">
          <div className="create-toolbar">
            <div className="toolbar-content">
              <button className="toolbar-btn">Text</button>
              <button className="toolbar-btn">Image</button>
              <button className="toolbar-btn">Video</button>
              <button className="toolbar-btn">Templates</button>
              <button className="toolbar-btn">Brand Kit</button>
            </div>
          </div>
          
          <div className="create-workspace">
            <div className="workspace-content">
              <p>Create Mode - Canvas Area</p>
            </div>
          </div>
          
          <div className="create-prompt-bottom">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              multiple
            />
            <button 
              className="attach-btn-create-mode" 
              title="Attach image or video"
              onClick={handleFileAttach}
            >
              <FaPaperclip className="action-icon" />
              {attachedFiles.length > 0 && (
                <span className="file-count">{attachedFiles.length}</span>
              )}
            </button>
            <div className="ai-prompt-container bottom-prompt">
              <div className="prompt-input-group">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the business content or strategy you need"
                  className="prompt-input"
                />
                <button 
                  onClick={handleGenerate} 
                  className="generate-button"
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </button>
              </div>
              
              {apiError && (
                <div className="api-error-message">
                  {apiError}
                </div>
              )}
              
              {generatedResponse && (
                <div className="generated-response">
                  <h3>AI Business Strategy:</h3>
                  <p>{generatedResponse}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <LoginOverlay
        isAuthenticated={false}
        error={null}
        loading={false}
        login={handleLogin}
        joinWaitlist={handleJoinWaitlist}
        onClose={closeAuthPopup}
        showOverlay={showAuthPopup}
        defaultMode="signup"
      />

    </div>
  );
}

export default OverviewPage;